import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  TrendingUp,
  Search,
  FileSpreadsheet,
  Package,
  AlertTriangle,
  X
} from "lucide-react";

const API = "/api";

export default function UrunSatisAnaliziPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [depolar, setDepolar] = useState([]);
  const [selectedDepo, setSelectedDepo] = useState("");
  const [baslangicTarihi, setBaslangicTarihi] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split("T")[0];
  });
  const [bitisTarihi, setBitisTarihi] = useState(() => {
    return new Date().toISOString().split("T")[0];
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [rapor, setRapor] = useState(null);
  const [varyantGorunumu, setVaryantGorunumu] = useState("varyantsiz");
  
  // Sayfalama
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  // Fotoğraf Modal
  const [photoModal, setPhotoModal] = useState({ open: false, itemCode: "", colorCode: "", colorName: "" });

  useEffect(() => {
    fetchDepolar();
  }, [token]);

  const fetchDepolar = async () => {
    try {
      const response = await axios.get(`${API}/nebim/depolar?token=${token}`);
      if (response.data.success) {
        setDepolar(response.data.depolar || []);
      }
    } catch (error) {
      console.error("Depolar alınamadı:", error);
    }
  };

  const fetchRapor = async () => {
    if (!selectedDepo) {
      toast.error("Lütfen depo seçin");
      return;
    }
    
    setLoading(true);
    setCurrentPage(1);
    try {
      const response = await axios.get(
        `${API}/nebim/rapor/urun-satis-analizi?token=${token}&baslangic=${baslangicTarihi}&bitis=${bitisTarihi}&depo=${selectedDepo}&varyant=${varyantGorunumu}`
      );
      if (response.data.success) {
        setRapor(response.data.rapor);
        if (response.data.rapor?.urunler?.length === 0) {
          toast.info("Seçilen tarih aralığında satış verisi bulunamadı");
        }
      } else {
        toast.error(response.data.error || "Rapor alınamadı");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Rapor hatası");
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = rapor?.urunler?.filter(urun => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (urun.ItemCode || "").toLowerCase().includes(term);
  }) || [];

  // Sayfalama
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const openPhotoModal = (itemCode) => {
    setPhotoModal({ open: true, itemCode, colorCode: "", colorName: "" });
  };

  return (
    <div className="space-y-6 fade-in" data-testid="urun-satis-analizi-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => window.history.back()}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ürün Satış Analizi</h1>
          <p className="text-slate-500 text-sm">Ürün bazlı satış detayları</p>
        </div>
      </div>

      {/* Filtreler */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div className="space-y-2">
              <Label>Başlangıç Tarihi</Label>
              <Input
                type="date"
                value={baslangicTarihi}
                onChange={(e) => setBaslangicTarihi(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Bitiş Tarihi</Label>
              <Input
                type="date"
                value={bitisTarihi}
                onChange={(e) => setBitisTarihi(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Depo <span className="text-red-500">*</span></Label>
              <Select value={selectedDepo} onValueChange={setSelectedDepo}>
                <SelectTrigger>
                  <SelectValue placeholder="Depo Seçin" />
                </SelectTrigger>
                <SelectContent>
                  {depolar.map((depo) => (
                    <SelectItem key={depo.kod} value={depo.kod}>
                      {depo.ad} ({depo.kod})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={fetchRapor} disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700">
                {loading ? "Yükleniyor..." : "Rapor Getir"}
              </Button>
            </div>
          </div>
          
          {/* Varyant Görünümü */}
          <div className="border rounded-lg p-4">
            <Label className="mb-3 block">Varyant Görünümü</Label>
            <RadioGroup value={varyantGorunumu} onValueChange={setVaryantGorunumu} className="flex gap-6">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="varyantsiz" id="varyantsiz" />
                <Label htmlFor="varyantsiz" className="cursor-pointer">Varyantsız</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="renk" id="renk" />
                <Label htmlFor="renk" className="cursor-pointer">Renk Bazında</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="renk_beden" id="renk_beden" />
                <Label htmlFor="renk_beden" className="cursor-pointer">Renk + Beden</Label>
              </div>
            </RadioGroup>
          </div>
        </CardContent>
      </Card>

      {/* Özet Kartları */}
      {rapor && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                  <Package className="w-4 h-4" />
                  Toplam Ürün Çeşidi
                </div>
                <p className="text-2xl font-bold">{rapor.ozet?.toplamUrunCesidi || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                  <TrendingUp className="w-4 h-4" />
                  Toplam Satış Miktarı
                </div>
                <p className="text-2xl font-bold">{rapor.ozet?.toplamSatisMiktari || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                  <TrendingUp className="w-4 h-4" />
                  Toplam Satış Tutarı
                </div>
                <p className="text-2xl font-bold text-emerald-600">
                  {(rapor.ozet?.toplamSatisTutari || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Arama ve Tablo */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Ürün Detayları</h3>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <Input
                      placeholder="Ürün kodu ara..."
                      value={searchTerm}
                      onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                      className="pl-10 w-64"
                    />
                  </div>
                  <Button variant="outline">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Excel
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium text-slate-600">Ürün Kodu</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-600">Renk</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-600">Beden</th>
                      <th className="text-right p-3 text-sm font-medium text-slate-600">Miktar</th>
                      <th className="text-right p-3 text-sm font-medium text-slate-600">Envanter</th>
                      <th className="text-right p-3 text-sm font-medium text-slate-600">Tutar (₺)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedProducts.map((urun, idx) => (
                      <tr key={idx} className="border-t hover:bg-slate-50">
                        <td className="p-3">
                          <button 
                            onClick={() => openPhotoModal(urun.ItemCode)}
                            className="text-blue-600 hover:underline font-mono text-sm"
                          >
                            {urun.ItemCode}
                          </button>
                        </td>
                        <td className="p-3 text-slate-600">{urun.ColorDescription || "-"}</td>
                        <td className="p-3 text-slate-600">{urun.ItemDim1Code || "-"}</td>
                        <td className="p-3 text-right font-medium">{urun.Qty1 || 0}</td>
                        <td className="p-3 text-right">
                          {(urun.Envanter || 0) < 0 ? (
                            <span className="flex items-center justify-end gap-1 text-orange-500">
                              <AlertTriangle className="w-4 h-4" />
                              {urun.Envanter}
                            </span>
                          ) : (
                            <span>{urun.Envanter || 0}</span>
                          )}
                        </td>
                        <td className="p-3 text-right font-medium text-emerald-600">
                          {(urun.Total || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Sayfalama */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <span className="text-slate-500 text-sm">
                    {filteredProducts.length} üründen {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, filteredProducts.length)} gösteriliyor
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Önceki
                    </Button>
                    <span className="px-3 py-1 text-sm">
                      {currentPage} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Sonraki
                    </Button>
                  </div>
                </div>
              )}

              {paginatedProducts.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  {searchTerm ? "Arama kriterine uygun ürün bulunamadı" : "Ürün bulunamadı"}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {!rapor && !loading && (
        <div className="text-center py-12">
          <TrendingUp className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">Tarih aralığı ve depo seçip "Rapor Getir" butonuna tıklayın</p>
        </div>
      )}

      {/* Fotoğraf Modal */}
      <Dialog open={photoModal.open} onOpenChange={(open) => setPhotoModal({ ...photoModal, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{photoModal.itemCode}</span>
              <Button variant="ghost" size="icon" onClick={() => setPhotoModal({ ...photoModal, open: false })}>
                <X className="w-4 h-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="bg-slate-100 rounded-lg p-4 flex items-center justify-center min-h-[300px]">
            <img 
              src={`${API}/nebim/urun-foto-proxy?token=${token}&itemCode=${photoModal.itemCode}`}
              alt={photoModal.itemCode}
              className="max-w-full max-h-[400px] object-contain rounded"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<div class="flex flex-col items-center text-slate-400"><svg class="w-16 h-16 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg><span>Fotoğraf bulunamadı</span></div>';
              }}
            />
          </div>
          <Button onClick={() => setPhotoModal({ ...photoModal, open: false })} className="w-full">
            Kapat
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
